import type { PropertyFilterPatch } from '~/core/entities/property/filterDefinitions';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { Ref } from 'vue';

import { normalizePropertyFilters } from '~/core/entities/property/filterDefinitions';
import { computed, ref } from 'vue';

export interface PropertySearchSnapshot {
	filters: PropertyFilters;
	confirmedFilters: PropertyFilters;
	revision: number;
	page: number;
}
export interface PropertySearchNavigation {
	build(filters: PropertyFilters): string;
	push(url: string): Promise<unknown>;
	currentUrl(): string;
	onError?(url: string, error: unknown): void;
}
function immutableFilters(input: object): PropertyFilters {
	const filters = normalizePropertyFilters(input);
	for (const value of Object.values(filters))
		if (Array.isArray(value)) Object.freeze(value);
	return Object.freeze(filters);
}
function sortedEntries(filters: object) {
	return Object.entries(filters).sort(([a], [b]) => a.localeCompare(b));
}
export function filterStateKey(filters: object): string {
	return JSON.stringify(sortedEntries(filters));
}
// Array order is irrelevant to the API, so equivalent requests share a key.
export function filterRequestKey(filters: object): string {
	return JSON.stringify(
		sortedEntries(filters).map(([key, value]) => [
			key,
			Array.isArray(value) ? [...value].sort() : value
		])
	);
}
export function createPropertySearchStore(
	snapshot: Ref<PropertySearchSnapshot> = ref({
		filters: {},
		confirmedFilters: {},
		revision: 0,
		page: 1
	})
) {
	let navigation: PropertySearchNavigation | undefined;
	let pending: Promise<void> | undefined;
	let generation = 0;
	let ownUrl: string | undefined;
	let navigating = false;
	let navigationGeneration = 0;
	let needsFlush = false;
	const filters = computed(() => snapshot.value.filters);
	const confirmedFilters = computed(() => snapshot.value.confirmedFilters);
	const revision = computed(() => snapshot.value.revision);
	function setFilters(input: object) {
		snapshot.value = { ...snapshot.value, filters: immutableFilters(input) };
	}
	function restore(filters: PropertyFilters, page = 1, url?: string) {
		const normalized = immutableFilters(filters);
		const own =
			navigating && url === ownUrl && generation === navigationGeneration;
		if (!own) {
			generation++;
			needsFlush = false;
		}
		const changed =
			filterStateKey(normalized) !==
			filterStateKey(snapshot.value.confirmedFilters);
		snapshot.value = {
			...snapshot.value,
			filters: own && needsFlush ? snapshot.value.filters : normalized,
			confirmedFilters: normalized,
			page,
			revision: snapshot.value.revision + (changed ? 1 : 0)
		};
	}
	function schedule(): Promise<void> {
		needsFlush = true;
		if (pending) return pending;
		pending = Promise.resolve()
			.then(async () => {
				while (needsFlush && navigation) {
					needsFlush = false;
					const url = navigation.build(snapshot.value.filters);
					if (url === navigation.currentUrl()) continue;
					navigationGeneration = generation;
					ownUrl = url;
					navigating = true;
					try {
						await navigation.push(url);
					} catch (error) {
						navigation?.onError?.(url, error);
						if (!needsFlush)
							snapshot.value = {
								...snapshot.value,
								filters: snapshot.value.confirmedFilters
							};
					} finally {
						navigating = false;
						ownUrl = undefined;
					}
				}
			})
			.finally(() => {
				pending = undefined;
			});
		return pending;
	}
	function patch(patch: PropertyFilterPatch) {
		return replace(
			normalizePropertyFilters({ ...snapshot.value.filters, ...patch })
		);
	}
	function replace(filters: PropertyFilters) {
		const normalized = immutableFilters(filters);
		if (filterStateKey(normalized) === filterStateKey(snapshot.value.filters))
			return pending ?? Promise.resolve();
		setFilters(normalized);
		return schedule();
	}
	function acceptResolvedLocation(
		filters: PropertyFilters,
		expectedRevision: number
	) {
		if (expectedRevision !== snapshot.value.revision) return;
		const resolved = immutableFilters({
			...snapshot.value.confirmedFilters,
			...filters
		});
		snapshot.value = {
			...snapshot.value,
			confirmedFilters: resolved,
			filters: pending || navigating ? snapshot.value.filters : resolved
		};
	}
	return {
		filters,
		confirmedFilters,
		revision,
		urlFor: (input: object) => navigation?.build(immutableFilters(input)),
		setNavigation(value: PropertySearchNavigation) {
			navigation = value;
		},
		restore,
		patch,
		replace,
		acceptResolvedLocation,
		// A filter or place gesture is still on its way to the URL.
		hasPendingNavigation: () => Boolean(pending) || navigating,
		dispose() {
			generation++;
			needsFlush = false;
			navigation = undefined;
		}
	};
}
export type PropertySearchStore = ReturnType<typeof createPropertySearchStore>;
